<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, ref, watch } from "vue"
import { useForm } from "vee-validate"
import { toTypedSchema } from "@vee-validate/yup"
import * as yup from "yup"
import { useI18n } from "vue-i18n"
import { storeToRefs } from "pinia"
import type { Building, BuildingParties } from "@types"
import { useBuildingStore } from "@core/stores/building-v2.store"

// ================================
// PROPS & EMITS
// ================================
const props = defineProps<{
    readonly: boolean
    /** Bâtiment en consultation seule : champs désactivés (pas `readonly`). */
    disabled?: boolean
}>()

// ================================
// COMPOSABLES
// ================================
const buildingStore = useBuildingStore()
const { building } = storeToRefs(buildingStore)

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            title: "Acteurs du bâtiment",
            gestionnaireName: "Gestionnaire",
            gestionnaireCode: "Code de gestion du gestionnaire",
            amoName: "AMO",
            amoCode: "Code de gestion de l'AMO",
            exploitantName: "Exploitant",
            exploitantCode: "Code de gestion de l'exploitant",
            save: "Sauvegarder",
            cancel: "Annuler",
            validation: {
                maxLength: "Le texte ne doit pas dépasser {max} caractères.",
            },
        },
    },
})

const ACTOR_FIELD_MAX = 255

const getValidationSchema = computed(() =>
    toTypedSchema(
        yup.object({
            gestionnaire_name: yup
                .string()
                .max(
                    ACTOR_FIELD_MAX,
                    t("validation.maxLength", { max: ACTOR_FIELD_MAX })
                )
                .optional(),
            gestionnaire_code: yup
                .string()
                .max(
                    ACTOR_FIELD_MAX,
                    t("validation.maxLength", { max: ACTOR_FIELD_MAX })
                )
                .optional(),
            amo_name: yup
                .string()
                .max(
                    ACTOR_FIELD_MAX,
                    t("validation.maxLength", { max: ACTOR_FIELD_MAX })
                )
                .optional(),
            amo_code: yup
                .string()
                .max(
                    ACTOR_FIELD_MAX,
                    t("validation.maxLength", { max: ACTOR_FIELD_MAX })
                )
                .optional(),
            exploitant_name: yup
                .string()
                .max(
                    ACTOR_FIELD_MAX,
                    t("validation.maxLength", { max: ACTOR_FIELD_MAX })
                )
                .optional(),
            exploitant_code: yup
                .string()
                .max(
                    ACTOR_FIELD_MAX,
                    t("validation.maxLength", { max: ACTOR_FIELD_MAX })
                )
                .optional(),
        })
    )
)

/**
 * Relie l'état d'erreur vee-validate à la prop PrimeVue `invalid`.
 */
function getPrimeInvalidFieldProps(state: { errors: string[] }) {
    return {
        props: {
            invalid: state.errors.length > 0,
        },
    }
}

const { handleSubmit, defineField, resetForm, errors } = useForm({
    validationSchema: getValidationSchema,
    initialValues: {
        gestionnaire_name: "",
        gestionnaire_code: "",
        amo_name: "",
        amo_code: "",
        exploitant_name: "",
        exploitant_code: "",
    },
})

const [gestionnaireName, gestionnaireNameAttrs] = defineField(
    "gestionnaire_name",
    getPrimeInvalidFieldProps
)
const [gestionnaireCode, gestionnaireCodeAttrs] = defineField(
    "gestionnaire_code",
    getPrimeInvalidFieldProps
)
const [amoName, amoNameAttrs] = defineField(
    "amo_name",
    getPrimeInvalidFieldProps
)
const [amoCode, amoCodeAttrs] = defineField(
    "amo_code",
    getPrimeInvalidFieldProps
)
const [exploitantName, exploitantNameAttrs] = defineField(
    "exploitant_name",
    getPrimeInvalidFieldProps
)
const [exploitantCode, exploitantCodeAttrs] = defineField(
    "exploitant_code",
    getPrimeInvalidFieldProps
)

function getFormValuesFromParties(p?: BuildingParties | null) {
    return {
        gestionnaire_name: p?.gestionnaire_name ?? "",
        gestionnaire_code: p?.gestionnaire_code ?? "",
        amo_name: p?.amo_name ?? "",
        amo_code: p?.amo_code ?? "",
        exploitant_name: p?.exploitant_name ?? "",
        exploitant_code: p?.exploitant_code ?? "",
    }
}

function syncFromBuilding(): void {
    resetForm({
        values: getFormValuesFromParties(building.value?.parties ?? null),
    })
}

watch(building, syncFromBuilding, { immediate: true, deep: true })

const loading = ref(false)
const errorMsg = ref("")

function resolveBuildingMongoId(b: Building): string {
    return b._id ?? b._id_building
}

const getHasChanged = computed(() => {
    const p = building.value?.parties
    return (
        gestionnaireName.value !== (p?.gestionnaire_name ?? "") ||
        gestionnaireCode.value !== (p?.gestionnaire_code ?? "") ||
        amoName.value !== (p?.amo_name ?? "") ||
        amoCode.value !== (p?.amo_code ?? "") ||
        exploitantName.value !== (p?.exploitant_name ?? "") ||
        exploitantCode.value !== (p?.exploitant_code ?? "")
    )
})

const onSubmit = handleSubmit(async (values) => {
    const b = building.value
    if (!b || props.readonly || props.disabled) return
    try {
        loading.value = true
        errorMsg.value = ""
        const id = resolveBuildingMongoId(b)
        await buildingStore.updateBuildingById(
            id,
            {
                "parties.gestionnaire_name": values.gestionnaire_name,
                "parties.gestionnaire_code": values.gestionnaire_code,
                "parties.amo_name": values.amo_name,
                "parties.amo_code": values.amo_code,
                "parties.exploitant_name": values.exploitant_name,
                "parties.exploitant_code": values.exploitant_code,
            } as Record<string, unknown>,
            { silent: true }
        )
    } catch (e) {
        errorMsg.value = (e as Error).message
    } finally {
        loading.value = false
    }
})

function onCancel(): void {
    resetForm({
        values: getFormValuesFromParties(building.value?.parties ?? null),
    })
}
</script>

<template>
    <BaseTitledCard class="building-info-general-actors w-full shadow-sm">
        <template #header>
            {{ t("title") }}
        </template>
        <template #content>
            <form
                class="grid grid-cols-1 md:grid-cols-2 gap-4"
                @submit.prevent="onSubmit"
            >
                <BaseInputWrapper
                    :label="t('gestionnaireName')"
                    input-id="building-info-general-actors-gestionnaire-name"
                    :error="errors.gestionnaire_name ?? ''"
                    unfloat
                >
                    <BaseInputText
                        v-model="gestionnaireName"
                        v-bind="gestionnaireNameAttrs"
                        input-id="building-info-general-actors-gestionnaire-name"
                        class="w-full"
                        :disabled="disabled"
                        :readonly="readonly && !disabled"
                    />
                </BaseInputWrapper>
                <BaseInputWrapper
                    :label="t('gestionnaireCode')"
                    input-id="building-info-general-actors-gestionnaire-code"
                    :error="errors.gestionnaire_code ?? ''"
                    unfloat
                >
                    <BaseInputText
                        v-model="gestionnaireCode"
                        v-bind="gestionnaireCodeAttrs"
                        input-id="building-info-general-actors-gestionnaire-code"
                        class="w-full"
                        :disabled="disabled"
                        :readonly="readonly && !disabled"
                    />
                </BaseInputWrapper>
                <BaseInputWrapper
                    :label="t('amoName')"
                    input-id="building-info-general-actors-amo-name"
                    :error="errors.amo_name ?? ''"
                    unfloat
                >
                    <BaseInputText
                        v-model="amoName"
                        v-bind="amoNameAttrs"
                        input-id="building-info-general-actors-amo-name"
                        class="w-full"
                        :disabled="disabled"
                        :readonly="readonly && !disabled"
                    />
                </BaseInputWrapper>
                <BaseInputWrapper
                    :label="t('amoCode')"
                    input-id="building-info-general-actors-amo-code"
                    :error="errors.amo_code ?? ''"
                    unfloat
                >
                    <BaseInputText
                        v-model="amoCode"
                        v-bind="amoCodeAttrs"
                        input-id="building-info-general-actors-amo-code"
                        class="w-full"
                        :disabled="disabled"
                        :readonly="readonly && !disabled"
                    />
                </BaseInputWrapper>
                <BaseInputWrapper
                    :label="t('exploitantName')"
                    input-id="building-info-general-actors-exploitant-name"
                    :error="errors.exploitant_name ?? ''"
                    unfloat
                >
                    <BaseInputText
                        v-model="exploitantName"
                        v-bind="exploitantNameAttrs"
                        input-id="building-info-general-actors-exploitant-name"
                        class="w-full"
                        :disabled="disabled"
                        :readonly="readonly && !disabled"
                    />
                </BaseInputWrapper>
                <BaseInputWrapper
                    :label="t('exploitantCode')"
                    input-id="building-info-general-actors-exploitant-code"
                    :error="errors.exploitant_code ?? ''"
                    unfloat
                >
                    <BaseInputText
                        v-model="exploitantCode"
                        v-bind="exploitantCodeAttrs"
                        input-id="building-info-general-actors-exploitant-code"
                        class="w-full"
                        :disabled="disabled"
                        :readonly="readonly && !disabled"
                    />
                </BaseInputWrapper>
                <div
                    v-if="getHasChanged && !readonly && !disabled"
                    class="md:col-span-2 flex flex-wrap justify-center gap-3"
                >
                    <BaseButton
                        type="button"
                        severity="secondary"
                        @click="onCancel"
                        :label="t('cancel')"
                    >
                    </BaseButton>
                    <BaseButton
                        type="submit"
                        :loading="loading"
                        :label="t('save')"
                    >
                    </BaseButton>
                </div>
                <BaseMessage
                    v-if="errorMsg"
                    severity="error"
                    class="md:col-span-2"
                    :closable="false"
                >
                    {{ errorMsg }}
                </BaseMessage>
            </form>
        </template>
    </BaseTitledCard>
</template>
